import { useEffect, useState } from "react";
import { api } from "../api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Upload, LogOut, Package, Tag, Image as ImgIcon, ShoppingCart } from "lucide-react";

const TABS = [
  { id: "products", label: "Products", icon: Package },
  { id: "inventory", label: "Inventory", icon: Package },
  { id: "discounts", label: "Discounts", icon: Tag },
  { id: "banners", label: "Banners", icon: ImgIcon },
  { id: "orders", label: "Orders", icon: ShoppingCart },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token"));
  const [email, setEmail] = useState("vashistharchit5@gmail.com");
  const [password, setPassword] = useState("Archit14&");
  const [tab, setTab] = useState("products");

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/login", { email, password });
      localStorage.setItem("admin_token", r.data.token);
      setToken(r.data.token);
      toast.success("Welcome");
    } catch {
      toast.error("Invalid credentials");
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };

  if (!token) {
    return (
      <div className="pt-32 min-h-screen flex items-start justify-center px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-md border border-white/10 p-12">
          <div className="overline text-muted-foreground">Restricted</div>
          <h1 className="serif text-4xl mt-3">Atelier Admin</h1>
          <div className="mt-10 space-y-4">
            <div>
              <div className="overline text-muted-foreground mb-2">Email</div>
              <input className="input-sharp" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
            </div>
            <div>
              <div className="overline text-muted-foreground mb-2">Password</div>
              <input type="password" className="input-sharp" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
            </div>
          </div>
          <button className="btn-sharp primary w-full mt-8" type="submit" data-testid="admin-login-btn">Sign in</button>
          <p className="mt-6 text-[11px] text-muted-foreground uppercase tracking-[0.18em] text-center">
            Default admin credentials are set locally.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-10">
        <div className="flex items-center justify-between border-b border-white/10 pb-6">
          <div>
            <div className="overline text-muted-foreground">Atelier</div>
            <h1 className="serif text-4xl mt-2">Dashboard</h1>
          </div>
          <button onClick={logout} className="text-xs uppercase tracking-[0.22em] flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="admin-logout-btn">
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="flex gap-8 mt-8 border-b border-white/10">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 py-4 text-xs uppercase tracking-[0.22em] border-b -mb-px ${
                tab === id ? "border-white text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`admin-tab-${id}`}
            >
              <Icon size={14} strokeWidth={1.2} />{label}
            </button>
          ))}
        </div>

        <div className="py-10">
          {tab === "products" && <ProductsTab />}
          {tab === "inventory" && <InventoryTab />}
          {tab === "discounts" && <DiscountsTab />}
          {tab === "banners" && <BannersTab />}
          {tab === "orders" && <OrdersTab />}
        </div>
      </div>
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="text-sm text-muted-foreground">{products.length} products</div>
        <button className="btn-sharp primary" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-product-btn">
          <Plus size={14} strokeWidth={1.5} className="mr-2" /> New product
        </button>
      </div>

      {showForm && (
        <ProductForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { load(); setShowForm(false); setEditing(null); }}
        />
      )}

      <table className="w-full text-sm" data-testid="products-table">
        <thead className="text-left text-muted-foreground text-xs uppercase tracking-[0.18em] border-b border-white/10">
          <tr><th className="py-4">Item</th><th>Category</th><th>Price</th><th>Stock</th><th>Featured</th><th></th></tr>
        </thead>
        <tbody className="divide-y divide-white/10">
          {products.map((p) => (
            <tr key={p.id}>
              <td className="py-4 flex items-center gap-4">
                {p.images?.[0] && <img src={p.images[0]} alt="" className="w-12 h-16 object-cover" />}
                <span className="serif text-lg">{p.name}</span>
              </td>
              <td className="capitalize">{p.category}</td>
              <td>${p.price.toFixed(2)}</td>
              <td className={p.stock < 5 ? "text-destructive" : ""}>{p.stock}</td>
              <td>{p.featured ? "★" : "—"}</td>
              <td className="text-right space-x-3">
                <button onClick={() => { setEditing(p); setShowForm(true); }} className="text-muted-foreground hover:text-foreground" data-testid={`edit-product-${p.id}`}><Edit3 size={14} /></button>
                <button onClick={() => del(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-product-${p.id}`}><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProductForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(() => initial || {
    name: "", description: "", price: 0, category: "women", images: [], sizes: [], colors: [], stock: 0, featured: false
  });
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setF({ ...f, images: [...f.images, url] });
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const save = async () => {
    const body = { ...f, price: parseFloat(f.price) || 0, stock: parseInt(f.stock) || 0 };
    try {
      if (initial?.id) await api.put(`/admin/products/${initial.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="border border-white/10 p-8 mb-8" data-testid="product-form">
      <div className="flex justify-between items-center mb-6">
        <h3 className="serif text-2xl">{initial ? "Edit product" : "New product"}</h3>
        <button onClick={onClose} className="text-muted-foreground text-xs uppercase tracking-[0.22em]">Close</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <input className="input-sharp" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pf-name" />
        <select className="input-sharp" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="pf-category">
          <option className="bg-background" value="women">Women</option>
          <option className="bg-background" value="men">Men</option>
          <option className="bg-background" value="accessories">Accessories</option>
        </select>
        <input className="input-sharp" type="number" step="0.01" placeholder="Price" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="pf-price" />
        <input className="input-sharp" type="number" placeholder="Stock" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} data-testid="pf-stock" />
        <input className="input-sharp md:col-span-2" placeholder="Sizes (comma separated)" value={f.sizes.join(",")} onChange={(e) => setF({ ...f, sizes: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} data-testid="pf-sizes" />
        <input className="input-sharp md:col-span-2" placeholder="Colours (comma separated)" value={f.colors.join(",")} onChange={(e) => setF({ ...f, colors: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} data-testid="pf-colors" />
        <textarea className="input-sharp md:col-span-2" rows={4} placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="pf-description" />
        <label className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]">
          <input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} data-testid="pf-featured" />
          Featured
        </label>
      </div>
      <div className="mt-6">
        <div className="overline text-muted-foreground mb-2">Images</div>
        <div className="flex gap-3 flex-wrap">
          {f.images.map((url, i) => (
            <div key={i} className="relative w-24 h-32 overflow-hidden">
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button onClick={() => setF({ ...f, images: f.images.filter((_, idx) => idx !== i) })} className="absolute top-1 right-1 bg-black/70 p-1"><Trash2 size={12} /></button>
            </div>
          ))}
          <label className="w-24 h-32 border border-white/20 border-dashed flex flex-col items-center justify-center text-muted-foreground text-xs cursor-pointer hover:border-white" data-testid="pf-upload-label">
            {uploading ? "…" : (<><Upload size={16} /><span className="mt-2">Upload</span></>)}
            <input type="file" className="hidden" accept="image/*" onChange={upload} />
          </label>
        </div>
        <input className="input-sharp mt-3" placeholder="Or paste image URL and press Enter"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.value) {
              setF({ ...f, images: [...f.images, e.target.value] });
              e.target.value = "";
            }
          }}
          data-testid="pf-image-url"
        />
      </div>
      <div className="flex gap-3 mt-8">
        <button className="btn-sharp primary" onClick={save} data-testid="pf-save">Save product</button>
        <button className="btn-sharp ghost" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function InventoryTab() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data.sort((a,b) => a.stock - b.stock))); }, []);

  const adjust = async (id, newStock) => {
    await api.put(`/admin/products/${id}`, { stock: parseInt(newStock) });
    setProducts((prev) => prev.map((p) => p.id === id ? { ...p, stock: parseInt(newStock) } : p));
    toast.success("Stock updated");
  };

  return (
    <div>
      <div className="overline text-muted-foreground mb-6">Inventory — sorted by lowest stock</div>
      <table className="w-full text-sm" data-testid="inventory-table">
        <thead className="text-left text-muted-foreground text-xs uppercase tracking-[0.18em] border-b border-white/10">
          <tr><th className="py-4">Product</th><th>Category</th><th>Stock</th><th>Status</th></tr>
        </thead>
        <tbody className="divide-y divide-white/10">
          {products.map((p) => (
            <tr key={p.id}>
              <td className="py-4 serif text-lg">{p.name}</td>
              <td className="capitalize">{p.category}</td>
              <td>
                <input type="number" defaultValue={p.stock} onBlur={(e) => adjust(p.id, e.target.value)} className="input-sharp w-24 py-1" data-testid={`inv-stock-${p.id}`} />
              </td>
              <td>
                {p.stock === 0 ? <span className="text-destructive">Out of stock</span> :
                  p.stock < 5 ? <span className="text-yellow-500">Low</span> : <span className="text-muted-foreground">In stock</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DiscountsTab() {
  const [codes, setCodes] = useState([]);
  const [code, setCode] = useState("");
  const [pct, setPct] = useState(10);

  const load = () => api.get("/admin/discounts").then((r) => setCodes(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post("/admin/discounts", { code, percent_off: parseInt(pct), active: true, usage_limit: 0 });
      setCode(""); setPct(10);
      toast.success("Code added");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    await api.delete(`/admin/discounts/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex gap-3 mb-8" data-testid="new-discount-form">
        <input className="input-sharp max-w-xs" placeholder="CODE" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="new-discount-code" />
        <input className="input-sharp max-w-[120px]" type="number" placeholder="%" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="new-discount-pct" />
        <button className="btn-sharp primary" onClick={add} data-testid="add-discount-btn">Create</button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-muted-foreground text-xs uppercase tracking-[0.18em] border-b border-white/10">
          <tr><th className="py-4">Code</th><th>% Off</th><th>Uses</th><th>Status</th><th></th></tr>
        </thead>
        <tbody className="divide-y divide-white/10">
          {codes.map((c) => (
            <tr key={c.id}>
              <td className="py-4 font-mono">{c.code}</td>
              <td>{c.percent_off}%</td>
              <td>{c.used_count}{c.usage_limit > 0 ? `/${c.usage_limit}` : ""}</td>
              <td>{c.active ? "Active" : "Inactive"}</td>
              <td className="text-right">
                <button onClick={() => del(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-discount-${c.id}`}><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BannersTab() {
  const [banners, setBanners] = useState([]);
  const [f, setF] = useState({ title: "", subtitle: "", cta_text: "Shop Now", cta_link: "/shop", image_url: "", active: true });

  const load = () => api.get("/admin/banners").then((r) => setBanners(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    await api.post("/admin/banners", f);
    setF({ title: "", subtitle: "", cta_text: "Shop Now", cta_link: "/shop", image_url: "", active: true });
    load();
    toast.success("Banner created");
  };

  const toggle = async (b) => {
    await api.put(`/admin/banners/${b.id}`, { ...b, active: !b.active });
    load();
  };

  const del = async (id) => {
    await api.delete(`/admin/banners/${id}`);
    load();
  };

  return (
    <div>
      <div className="border border-white/10 p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="new-banner-form">
        <input className="input-sharp" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="banner-title" />
        <input className="input-sharp" placeholder="Subtitle" value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} data-testid="banner-subtitle" />
        <input className="input-sharp" placeholder="CTA text" value={f.cta_text} onChange={(e) => setF({ ...f, cta_text: e.target.value })} />
        <input className="input-sharp" placeholder="CTA link" value={f.cta_link} onChange={(e) => setF({ ...f, cta_link: e.target.value })} />
        <input className="input-sharp md:col-span-2" placeholder="Image URL" value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} />
        <button className="btn-sharp primary md:col-span-2" onClick={add} data-testid="add-banner-btn">Create banner</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {banners.map((b) => (
          <div key={b.id} className="border border-white/10">
            {b.image_url && <img src={b.image_url} alt="" className="w-full h-48 object-cover" />}
            <div className="p-5">
              <div className="serif text-2xl">{b.title}</div>
              <div className="text-sm text-muted-foreground mt-1">{b.subtitle}</div>
              <div className="flex justify-between items-center mt-5">
                <button onClick={() => toggle(b)} className="text-xs uppercase tracking-[0.18em] text-muted-foreground" data-testid={`toggle-banner-${b.id}`}>
                  {b.active ? "Active — click to disable" : "Inactive — click to activate"}
                </button>
                <button onClick={() => del(b.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)); }, []);

  return (
    <div>
      <div className="overline text-muted-foreground mb-6">{orders.length} transactions</div>
      <table className="w-full text-sm" data-testid="orders-table">
        <thead className="text-left text-muted-foreground text-xs uppercase tracking-[0.18em] border-b border-white/10">
          <tr><th className="py-4">Session</th><th>Email</th><th>Amount</th><th>Status</th><th>Date</th></tr>
        </thead>
        <tbody className="divide-y divide-white/10">
          {orders.map((o) => (
            <tr key={o.id}>
              <td className="py-4 font-mono text-xs">{o.session_id?.slice(0, 20)}…</td>
              <td>{o.email || "—"}</td>
              <td>${o.amount.toFixed(2)}</td>
              <td className={o.payment_status === "paid" ? "" : "text-muted-foreground"}>{o.payment_status}</td>
              <td className="text-muted-foreground text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
